import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';
import InventoryScheduleEditor from './InventoryScheduleEditor';

const StoreDetails = ({ onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentStore, setCurrentStore] = useState(null);
  const [tableExists, setTableExists] = useState(true);
  
  // Form state
  const [storeData, setStoreData] = useState({
    store_name: '',
    address: '',
    phone: '',
    email: '',
    locations: [
      'Walk-in Cooler',
      'Walk-in Freezer',
      'Kitchen Prep Area',
      'Kitchen Line',
      'Dry Storage',
      'Bar Area'
    ],
    accounting_categories: [
      'Food',
      'Liquor', 
      'Beer',
      'Wine',
      'Cleaning Supplies',
      'Paper Products',
      'Equipment',
      'Utilities',
      'Labor',
      'Other'
    ]
  });

  // Load existing store details on component mount
  useEffect(() => {
    loadCurrentStore();
  }, []);

  const loadCurrentStore = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No user logged in');
        setLoading(false);
        return;
      }

      // Get user's store
      const { data: storeUserData, error: storeError } = await supabase
        .from('store_users')
        .select(`
          store_id,
          role,
          stores (
            id, name, address, phone, email
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (storeError) {
        console.error('Error loading store:', storeError);
        setLoading(false);
        return;
      }

      if (storeUserData && storeUserData.stores) {
        setCurrentStore(storeUserData.stores);
        
        // Set the store data from the stores table (with proper null safety)
        setStoreData(prev => ({
          ...prev,
          store_name: (storeUserData.stores.name || '').toString(),
          address: (storeUserData.stores.address || '').toString(),
          phone: (storeUserData.stores.phone || '').toString(),
          email: (storeUserData.stores.email || '').toString()
        }));

        // Try to load additional store details
        await loadStoreDetails(storeUserData.stores.id);
      }
    } catch (error) {
      console.error('Error loading current store:', error);
      setLoading(false);
    }
  };

  const loadStoreDetails = async (storeId) => {
    try {
      const { data, error } = await supabase
        .from('store_details')
        .select('*')
        .eq('store_id', storeId)
        .single();

      if (data && !error) {
        setStoreData(prev => ({
          ...prev,
          locations: (Array.isArray(data.locations) ? data.locations : prev.locations)
            .map(loc => {
              // Handle both old format (objects) and new format (strings)
              if (typeof loc === 'string') {
                return loc;
              } else if (loc && typeof loc === 'object' && loc.name) {
                return loc.name;
              } else {
                return '';
              }
            })
            .filter(loc => loc && loc.trim().length > 0),
          accounting_categories: (Array.isArray(data.accounting_categories) ? data.accounting_categories : prev.accounting_categories)
            .map(cat => (cat || '').toString())
        }));
      }
    } catch (error) {
      // Handle different types of errors
      if (error && error.code === 'PGRST116') {
        console.log('No existing store details found, using defaults');
      } else if (error && error.message && error.message.includes('relation "store_details" does not exist')) {
        console.warn('store_details table does not exist. Please run the database setup SQL.');
        setTableExists(false);
      } else {
        console.error('Error loading store details:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStoreInfoChange = (field, value) => {
    setStoreData(prev => ({
      ...prev,
      [field]: (value || '').toString()
    }));
  };

  const handleLocationChange = (index, value) => {
    const newLocations = [...storeData.locations];
    newLocations[index] = (value || '').toString();
    setStoreData(prev => ({
      ...prev,
      locations: newLocations
    }));
  };

  const handleCategoryChange = (index, value) => {
    const newCategories = [...storeData.accounting_categories];
    newCategories[index] = (value || '').toString();
    setStoreData(prev => ({
      ...prev,
      accounting_categories: newCategories
    }));
  };

  const addCategory = () => {
    setStoreData(prev => ({
      ...prev,
      accounting_categories: [...prev.accounting_categories, '']
    }));
  };

  const removeCategory = (index) => {
    const newCategories = storeData.accounting_categories.filter((_, i) => i !== index);
    setStoreData(prev => ({
      ...prev,
      accounting_categories: newCategories
    }));
  };

  const addLocation = () => {
    setStoreData(prev => ({
      ...prev,
      locations: [...prev.locations, '']
    }));
  };

  const removeLocation = (index) => {
    const newLocations = storeData.locations.filter((_, i) => i !== index);
    setStoreData(prev => ({
      ...prev,
      locations: newLocations
    }));
  };

  const handleSave = async () => {
    // Validation (with null safety)
    if (!(storeData.store_name || '').toString().trim()) {
      alert('Store Name is required');
      return;
    }

    if (!currentStore) {
      alert('Store information not loaded. Please refresh the page.');
      return;
    }

    // Filter out empty locations (with improved null/undefined safety)
    const validLocations = storeData.locations.filter(loc => 
      loc && 
      typeof loc === 'string' && 
      loc.trim().length > 0
    );
    
    // Filter out empty categories (with improved null/undefined safety)
    const validCategories = storeData.accounting_categories.filter(cat => 
      cat && 
      typeof cat === 'string' && 
      cat.trim().length > 0
    );

    setSaving(true);
    try {
      // First, update the main store information (with null safety)
      const { error: storeError } = await supabase
        .from('stores')
        .update({
          name: (storeData.store_name || '').toString().trim(),
          address: (storeData.address || '').toString().trim(),
          phone: (storeData.phone || '').toString().trim(),
          email: (storeData.email || '').toString().trim()
        })
        .eq('id', currentStore.id);

      if (storeError) {
        console.error('Error updating store:', storeError);
        alert('Error updating store information: ' + storeError.message);
        return;
      }

      // Then save the additional store details
      const detailsData = {
        store_id: currentStore.id,
        locations: validLocations,
        accounting_categories: validCategories,
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('store_details')
        .select('id')
        .eq('store_id', currentStore.id)
        .single();

      let result;
      if (existingData) {
        // Update existing record
        result = await supabase
          .from('store_details')
          .update(detailsData)
          .eq('store_id', currentStore.id);
      } else {
        // Insert new record
        result = await supabase
          .from('store_details')
          .insert([detailsData]);
      }

      if (result.error) {
        console.error('Supabase error:', result.error);
        
        // Check if the error is due to missing table
        if (result.error.message && result.error.message.includes('relation "store_details" does not exist')) {
          setTableExists(false);
          alert('The store details table is not set up yet. Please run the database setup SQL first. You can find the setup instructions at /fix-store-details.html');
        } else {
          alert('Error saving store details: ' + result.error.message);
        }
        return;
      }

      alert('Store details saved successfully!');
      
      if (onSave) {
        onSave({
          ...storeData,
          id: currentStore.id
        });
      }

    } catch (error) {
      console.error('Error saving store details:', error);
      alert('Error saving store details: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading store details...</p>
      </div>
    );
  }

  return (
    <div className="store-details">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Store Details & Configuration</h5>
              <p className="text-muted mb-0">Configure your store information and inventory organization</p>
            </div>
            {!tableExists && (
              <div className="alert alert-warning m-3" role="alert">
                <strong>⚠️ Database Setup Required:</strong> The store details table is not set up yet. 
                <a href="/fix-store-details.html" className="alert-link ms-2">Click here to set up the database</a>
              </div>
            )}
            <div className="card-body">
              
              {/* Basic Store Information */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <label className="form-label">Store Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={storeData.store_name}
                    onChange={(e) => handleStoreInfoChange('store_name', e.target.value)}
                    placeholder="Enter your store name"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Address (Optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={storeData.address}
                    onChange={(e) => handleStoreInfoChange('address', e.target.value)}
                    placeholder="Enter store address"
                  />
                </div>
              </div>

              {/* Inventory Locations */}
              <div className="mb-4">
                <h6>Inventory Locations</h6>
                <p className="text-muted small">Define where you keep different types of inventory items. This helps AI automatically assign items to appropriate locations.</p>
                
                <div className="row">
                  {storeData.locations.map((location, index) => (
                    <div key={index} className="col-md-3 mb-2">
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          value={location}
                          onChange={(e) => handleLocationChange(index, e.target.value)}
                          placeholder="Location name"
                        />
                        {storeData.locations.length > 1 && (
                          <button
                            className="btn btn-outline-danger"
                            type="button"
                            onClick={() => removeLocation(index)}
                          >
                            <i className="feather icon-x"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="col-md-3 mb-2">
                    <button
                      className="btn btn-outline-primary btn-sm w-100"
                      onClick={addLocation}
                    >
                      <i className="feather icon-plus me-1"></i>
                      Add Location
                    </button>
                  </div>
                </div>
              </div>

              {/* Accounting Categories */}
              <div className="mb-4">
                <h6>Accounting Categories</h6>
                <p className="text-muted small">Define categories for expense tracking. AI will automatically assign items to appropriate categories.</p>
                
                <div className="row">
                  {storeData.accounting_categories.map((category, index) => (
                    <div key={index} className="col-md-3 mb-2">
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          value={category}
                          onChange={(e) => handleCategoryChange(index, e.target.value)}
                          placeholder="Category name"
                        />
                        {storeData.accounting_categories.length > 1 && (
                          <button
                            className="btn btn-outline-danger"
                            type="button"
                            onClick={() => removeCategory(index)}
                          >
                            <i className="feather icon-x"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="col-md-3 mb-2">
                    <button
                      className="btn btn-outline-primary btn-sm w-100"
                      onClick={addCategory}
                    >
                      <i className="feather icon-plus me-1"></i>
                      Add Category
                    </button>
                  </div>
                </div>
              </div>

              {/* Inventory / End of Period Schedule */}
              <div className="mb-4">
                <h6>End of Period Schedule</h6>
                <p className="text-muted small">
                  How often you close out an accounting period. Used to auto-name EOP reports
                  and to warn you when a count doesn’t line up with the scheduled period end.
                </p>
                <InventoryScheduleEditor
                  storeId={currentStore?.id}
                />
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-between">
                <button 
                  className="btn btn-secondary" 
                  onClick={onCancel}
                  disabled={saving}
                >
                  <i className="feather icon-x me-1"></i>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="feather icon-save me-1"></i>
                      Save Store Details
                    </>
                  )}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreDetails;
